/**
 * Helpers for driving the DocsBot chat widget from inside a page.
 *
 * The widget is mounted once per full page load by resources/views/vue-layouts/app.blade.php,
 * on the core site with the core bot and on each state hub with that hub's own bot (see
 * docsBotWidgetId() in app/helpers.php); a hub with no bot configured gets none of this.
 * Inertia navigation never remounts it, which is what lets an in-page button drive the
 * same persistent conversation.
 *
 * Two things make the readiness check less obvious than it looks:
 *
 *  1. Until chat.js finishes downloading, `window.DocsBotAI` is only the inline stub the
 *     blade snippet defines — it has `init` but none of the control methods. So we check
 *     for the method we need, never just for the object.
 *  2. `addUserMessage()` logs a warning and resolves false if it runs before the widget has
 *     finished mounting. Waiting for the script alone is not enough; we wait for the mount
 *     promise the blade parks on `window.docsBotReady`.
 *  3. "The script has run" and "the widget is up" are **different moments, in either order**.
 *     chat.js defines the whole API as soon as it executes, but its `mount()` waits for
 *     `document.readyState === 'complete'` before creating `#docsbotai-root`. So on any repeat
 *     visit — chat.js served from cache, executing while the document is still parsing — the
 *     full API exists for a while with no widget in the DOM. Readiness therefore means
 *     `docsBotMounted()`: the API *and* the shadow root. Anything that reaches into the shadow
 *     tree (the panel watcher, the analytics listeners) is wrong to trust the API alone.
 */

const DEFAULT_TIMEOUT = 8000;
/**
 * Patience for a standing subscription rather than a click. A click has a person waiting on it
 * and must fail fast; a watcher only has to outlast the page's own load, which on a long
 * resource index over a slow connection is well past DEFAULT_TIMEOUT. Bounded all the same, so
 * nothing polls forever where the widget is never coming (hub with no bot, ad blocker).
 */
export const WATCH_TIMEOUT = 60000;
const POLL_INTERVAL = 100;

/**
 * Race a widget promise against a timeout. Every call goes through this: the widget's
 * open/addUserMessage promises resolve off internal "complete" events, and a missed event
 * would otherwise leave a caller (and a button's busy state) waiting forever.
 */
function withTimeout(promise, timeout) {
    return Promise.race([
        Promise.resolve(promise),
        new Promise(resolve => window.setTimeout(() => resolve(false), timeout)),
    ]);
}

/** True once chat.js has replaced the inline stub with the real widget API. */
export function docsBotLoaded() {
    return typeof window !== 'undefined' && typeof window.DocsBotAI?.open === 'function';
}

/**
 * True once the widget is not just loaded but *mounted* — the API is there and the shadow tree
 * exists. This, not `docsBotLoaded()`, is what readiness means; see note 3 in the file header.
 */
export function docsBotMounted() {
    return docsBotLoaded() && docsBotShadowRoot() !== null;
}

/** Resolve true as soon as the widget is mounted, false if it has not been within `timeout`. */
function whenDocsBotMounted(timeout) {
    return new Promise(resolve => {
        if (docsBotMounted()) {
            resolve(true);
            return;
        }

        const poll = window.setInterval(() => {
            if (!docsBotMounted()) return;
            window.clearInterval(poll);
            resolve(true);
        }, POLL_INTERVAL);

        window.setTimeout(() => {
            window.clearInterval(poll);
            resolve(false);
        }, timeout);
    });
}

/**
 * Resolve true once the widget is loaded and mounted, false if that never happens
 * within `timeout` (hub with no bot, ad blocker, offline).
 */
export function whenDocsBotReady(timeout = DEFAULT_TIMEOUT) {
    if (typeof window === 'undefined') return Promise.resolve(false);
    if (docsBotMounted()) return Promise.resolve(true);

    // The mount promise is the earliest trustworthy signal, so wait on it where the layout
    // parked one — but confirm the widget against the DOM either way. `docsBotReady` resolving
    // is not proof the shadow tree is queryable yet, and its absence (older cached layout, or
    // the block never rendered) is no reason to stop watching for a widget that may still turn up.
    const mounted = typeof window.docsBotReady?.then === 'function'
        ? Promise.resolve(window.docsBotReady)
            .then(ok => ok !== false && whenDocsBotMounted(timeout))
        : whenDocsBotMounted(timeout);

    return withTimeout(mounted, timeout);
}

/**
 * Open the chat panel, optionally seeding it with a page-specific question.
 *
 * @param {Object}  [opts]
 * @param {?string} [opts.question] Question to put in the chat on the user's behalf.
 * @param {boolean} [opts.send]     Send the question immediately (true) or just pre-fill it.
 * @param {number}  [opts.timeout]
 * @returns {Promise<boolean>} false when the widget is unavailable, so callers can degrade.
 */
export async function openDocsBot({ question = null, send = true, timeout = DEFAULT_TIMEOUT } = {}) {
    const ready = await whenDocsBotReady(timeout);
    if (!ready) return false;

    const widget = window.DocsBotAI;

    if (question && typeof widget.addUserMessage === 'function') {
        // addUserMessage() opens the panel itself when sending; when only pre-filling it
        // does not, so open first or the seeded question would sit in a closed widget.
        if (!send) await withTimeout(widget.open(), timeout);
        await withTimeout(widget.addUserMessage(question, send), timeout);
        return true;
    }

    await withTimeout(widget.open(), timeout);
    return true;
}

/**
 * Body class that hides the floating launcher. The rule lives in
 * components/AskDocsBotButton.vue; it has to reach through the widget's shadow root, which
 * is only possible via the parts the widget exposes (part="button" on the launcher).
 */
export const LAUNCHER_HIDDEN_CLASS = 'docsbot-launcher-hidden';

/**
 * The widget's shadow host is #docsbotai-root itself, and it is mounted mode: 'open'.
 *
 * Exported because it is the only handle on the widget's internals: every click, keystroke and
 * link inside the chat is retargeted at this boundary, so anything that needs the real event
 * target (Helpers/DocsBotAnalytics.js) has to attach its listeners to this root rather than to
 * `document`.
 */
export function docsBotShadowRoot() {
    return document.getElementById('docsbotai-root')?.shadowRoot ?? null;
}

/**
 * True while the chat panel is on screen. Keyed on part="wrapper" — the panel is only
 * rendered while open, and a part name is a documented styling hook, unlike the internal
 * class names next to it.
 *
 * DocsBotAI.isChatbotOpen is not usable here: it is written by the API methods only, so it
 * goes stale the moment a user closes the panel with the launcher or Escape.
 */
export function docsBotPanelOpen() {
    return Boolean(docsBotShadowRoot()?.querySelector('[part="wrapper"]'));
}

/**
 * Report whether the chat panel is open, now and on every later change — including changes we
 * did not cause, such as the visitor closing the panel with the launcher or the Escape key.
 *
 * @param {Function} onChange called with true/false
 * @returns {Function} cleanup
 */
export function watchDocsBotPanel(onChange) {
    if (typeof document === 'undefined') return () => {};

    let observer = null;
    let stopped = false;

    const report = () => onChange(docsBotPanelOpen());

    report();

    // WATCH_TIMEOUT rather than the click default: giving up here is permanent — there is no
    // retry and no second chance to attach — and it takes the pill's Close state, the launcher
    // restore and the FullStory open event down with it.
    whenDocsBotReady(WATCH_TIMEOUT).then(ready => {
        if (stopped || !ready) return;

        // Guaranteed by docsBotMounted() above; kept so a future change to readiness cannot
        // silently turn this back into a dead watcher.
        const shadowRoot = docsBotShadowRoot();
        if (!shadowRoot) return;

        // The panel is added to / removed from the shadow tree as it opens and closes.
        observer = new MutationObserver(report);
        observer.observe(shadowRoot, { childList: true, subtree: true });
        report();
    });

    return () => {
        stopped = true;
        observer?.disconnect();
        observer = null;
    };
}

/** Close the chat panel. Safe to call when it is already closed. */
export async function closeDocsBot({ timeout = DEFAULT_TIMEOUT } = {}) {
    if (!docsBotMounted()) return false;

    await withTimeout(window.DocsBotAI.close(), timeout);
    return true;
}

/**
 * Hide the floating launcher for as long as the caller is on the page, but only while the
 * chat panel is closed.
 *
 * The launcher doubles as the panel's close button (the panel header has a reset control,
 * not a close one), so hiding it outright would leave a teacher who opened the chat with no
 * way back out except the Escape key — which touch devices do not have. Instead the bubble
 * stays out of the way until the chat is open, then reappears to close it.
 *
 * @returns {Function} cleanup — call on unmount to stop watching and restore the launcher.
 */
export function hideDocsBotLauncherWhileClosed() {
    if (typeof document === 'undefined') return () => {};

    const stopWatching = watchDocsBotPanel(panelOpen => {
        document.body.classList.toggle(LAUNCHER_HIDDEN_CLASS, !panelOpen);
    });

    return () => {
        stopWatching();
        document.body.classList.remove(LAUNCHER_HIDDEN_CLASS);
    };
}
